import apiClient from "@/services/axios";
import { showApiError } from "@/utilities/showApiError";
import JsPDF from "jspdf";
import "jspdf-autotable";

const TRIMESTER_NAMES_AR = {
  1: "محضر جلسة الثلاثي الأول",
  2: "محضر جلسة الثلاثي الثاني",
  3: "محضر جلسة الثلاثي الثالث",
};

const TRIMESTER_NAMES_FR = {
  1: "Procès-verbal de la première trimestre",
  2: "Procès-verbal de la deuxième trimestre",
  3: "Procès-verbal de la troisième trimestre",
};

const ARABIC_CHAR_REGEX = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;

function containsArabic(text) {
  return ARABIC_CHAR_REGEX.test(String(text ?? ""));
}

function getPdfFontForText(text, defaultFontFamily) {
  return containsArabic(text) ? "Amiri" : defaultFontFamily;
}

function getCellText(cell) {
  if (!cell?.text) return "";
  return Array.isArray(cell.text) ? cell.text.join(" ") : String(cell.text);
}
function drawCenteredArabicLabelValue(doc, label, value, centerX, y, options = {}) {
  const {
    labelFont = "Amiri",
    fontSize = 13,
    fontStyle = "Bold",
    separator = " : ",
  } = options;
  const displayValue = value || "----";
  const valueFont = getPdfFontForText(displayValue, labelFont);

  doc.setFontSize(fontSize);
  doc.setFont(valueFont, fontStyle);
  const valueWidth = doc.getTextWidth(displayValue);
  doc.setFont(labelFont, fontStyle);
  const labelWidth = doc.getTextWidth(label);
  const sepWidth = doc.getTextWidth(separator);
  const totalWidth = valueWidth + sepWidth + labelWidth;
  const leftX = centerX - totalWidth / 2;

  doc.setFont(valueFont, fontStyle);
  doc.text(displayValue, leftX, y);
  doc.setFont(labelFont, fontStyle);
  doc.text(separator, leftX + valueWidth, y);
  doc.text(label, leftX + valueWidth + sepWidth, y);
}

async function imageUrlToBase64(url) {
  try {
    if (!url) throw new Error("no URL");
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null;
  }
}

function formatMarkNumber(value, showExempt = false) {
  if (value == null || value === "" || isNaN(Number(value))) {
    return showExempt ? "معفى" : "--.--";
  }
  const num = Number(value);
  const integerPart = Math.floor(Math.abs(num));
  const decimalPart = (Math.abs(num) % 1).toFixed(2).substring(1);
  return integerPart.toString().padStart(2, "0") + decimalPart;
}

function isSubjectExempt(subjectRow) {
  if (!subjectRow?.exemptions) return false;
  return Object.values(subjectRow.exemptions).some(Boolean) && subjectRow.moyenne == null;
}

function formatSubjectMark(subjectRow) {
  if (subjectRow?.moyenne != null) {
    return formatMarkNumber(subjectRow.moyenne);
  }
  if (isSubjectExempt(subjectRow)) {
    return formatMarkNumber(null, true);
  }
  return formatMarkNumber(null);
}

function calculateWeightedTotal(subjectMoyennes = []) {
  const total = subjectMoyennes.reduce((sum, row) => {
    if (row.moyenne == null) return sum;
    return sum + Number(row.moyenne) * Number(row.coef || 0);
  }, 0);
  return Math.round(total * 100) / 100;
}

function getSubjectRows(studentsData = []) {
  return studentsData[0]?.subjectMoyennes || [];
}

function getSchoolYearName(settings) {
  const schoolYear = settings?.schoolarYears?.find(
    (el) => el._id === settings?.activeSchoolarYear
  );
  const startYear = schoolYear?.start
    ? new Date(schoolYear.start).getFullYear()
    : new Date().getFullYear();
  const endYear = schoolYear?.end
    ? new Date(schoolYear.end).getFullYear()
    : startYear + 1;
  return `${endYear}/${startYear}`;
}

function getSchoolTypeLabel(level, schoolName, language) {
  if (language === "fr") {
    return `${level <= 9 ? "Collège privé" : "Institut privé"} ${schoolName || ""}`.trim();
  }
  const prefix = level <= 9 ? "المدرسة الإعدادية الخاصة" : "المعهد الخاص";
  return `${prefix} ${schoolName || ""}`.trim();
}

function getSchoolTypePrefix(level, language) {
  if (language === "fr") {
    return level <= 9 ? "Collège privé " : "Institut privé ";
  }
  return level <= 9 ? "المدرسة الإعدادية الخاصة " : "المعهد الخاص ";
}

function drawSchoolTypeHeader(doc, { level, schoolName, language, fontFamily, x, y, align }) {
  const prefix = getSchoolTypePrefix(level, language);
  const schoolFontFamily = getPdfFontForText(schoolName, fontFamily);
  doc.setFont(fontFamily, "Bold");
  const prefixWidth = doc.getTextWidth(prefix);
  doc.setFont(schoolFontFamily, "Bold");
  const schoolNameWidth = schoolName ? doc.getTextWidth(schoolName) : 0;

  if (align === "right") {
    // RTL: school type on the right, school name after it (to the left)
    const prefixX = x - prefixWidth;
    const schoolNameX = x - prefixWidth - schoolNameWidth;
    doc.setFont(fontFamily, "Bold");
    doc.text(prefix, prefixX, y);
    if (schoolName) {
      doc.setFont(schoolFontFamily, "Bold");
      doc.text(schoolName, schoolNameX, y);
    }
    return;
  }

  // LTR: school type first, then school name
  doc.setFont(fontFamily, "Bold");
  doc.text(prefix, x, y);
  if (schoolName) {
    doc.setFont(schoolFontFamily, "Bold");
    doc.text(schoolName, x + prefixWidth, y);
  }
}

function sortStudentsByRank(studentsData = []) {
  return [...studentsData].sort((a, b) => {
    const rankA = a.rank == null ? Number.MAX_SAFE_INTEGER : a.rank;
    const rankB = b.rank == null ? Number.MAX_SAFE_INTEGER : b.rank;
    return rankA - rankB;
  });
}

function wrapPdfText(doc, text, maxWidth, fontFamily, fontStyle = "Bold", fontSize = 7) {
  if (!text) return [];
  if (fontFamily) {
    doc.setFont(fontFamily, fontStyle);
    doc.setFontSize(fontSize);
  }
  return doc.splitTextToSize(String(text), Math.max(maxWidth, 1));
}

function getWrappedLineHeight(fontSize) {
  return fontSize * 0.42;
}

function estimateWrappedHeadHeight(doc, text, cellWidth, defaultFontFamily, fontSize = 7) {
  const fontFamily = getPdfFontForText(text, defaultFontFamily);
  const lines = wrapPdfText(doc, text, Math.max(cellWidth - 2, 8), fontFamily, "Bold", fontSize);
  return Math.max(24, lines.length * getWrappedLineHeight(fontSize) + 8);
}

function drawWrappedCellText(doc, text, cell, options = {}) {
  const {
    defaultFontFamily,
    fontSize = 7,
    fontStyle = "Bold",
    padding = 1,
    bottomReserved = 5,
  } = options;
  const fontFamily = getPdfFontForText(text, defaultFontFamily);
  const lines = wrapPdfText(
    doc,
    text,
    Math.max(cell.width - padding * 2, 8),
    fontFamily,
    fontStyle,
    fontSize
  );
  if (!lines.length) return;

  const lineHeight = getWrappedLineHeight(fontSize);
  const totalHeight = lines.length * lineHeight;
  const startY =
    cell.y +
    Math.max(2, (cell.height - bottomReserved - totalHeight) / 2) +
    lineHeight * 0.75;
  const x = cell.x + cell.width / 2;

  lines.forEach((line, index) => {
    doc.text(line, x, startY + index * lineHeight, { align: "center" });
  });
}

export default {
  data() {
    return {
      pvGovLogoBase64: null,
    };
  },
  async created() {
    this.pvGovLogoBase64 = await imageUrlToBase64("/resources/images/govImg.png");
  },
  methods: {
    async fetchClassPvData(classRoomId, trimester) {
      const response = await apiClient.get(
        `/bulletin/college/pv/moyennes/all/${classRoomId}/${trimester}`
      );
      return response.data;
    },

    async printClassPv(trimester, language = "ar") {
      if (!this.selectedClasse || !trimester) {
        this.$message.warning(
          this.$t("bulletin.selectStudentAndTrimester") ||
            "Please select a class and trimester"
        );
        return;
      }

      this.printLoading = true;
      try {
        const data = await this.fetchClassPvData(this.selectedClasse, trimester);
        if (!data?.studentsData?.length) {
          this.$message.error(this.$t("error.erreur"));
          return;
        }

        // French PV disabled — Arabic only for now
        // const doc =
        //   language === "fr"
        //     ? this.generatePvPdfFr(data)
        //     : this.generatePvPdfAr(data);
        const doc = this.generatePvPdfAr(data);
        const className = data.classroom?.name || "classe";
        doc.save(`PV_${className}_${trimester}.pdf`);
        this.$message.success(
          this.$t("bulletin.printSuccess") || "PDF generated successfully"
        );
      } catch (error) {
        showApiError(this, error);
      } finally {
        this.printLoading = false;
      }
    },

    generatePvPdfAr(data) {
      return this.generatePvPdf(data, "ar");
    },

    // generatePvPdfFr(data) {
    //   return this.generatePvPdf(data, "fr");
    // },

    generatePvPdf(data, language) {
      const doc = new JsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });
      const labels = this.getPvLabels(language);
      const pageWidth = doc.internal.pageSize.getWidth();
      const leftMargin = 10;
      const rightMargin = pageWidth - 10;
      const centerX = pageWidth / 2;
      const schoolDetails = this.schoolDetails || {};
      const delegation = schoolDetails.delegeRegional || "";
      const schoolName =
        language === "fr"
          ? schoolDetails.FrenchName || schoolDetails.name || ""
          : schoolDetails.ArabicName || schoolDetails.name || "";
      const schoolYear = getSchoolYearName(this.settings);
      const level = data.classroom?.level;
      const className = data.classroom?.name || "";
      const trimester = Number(data.trimester);
      const studentsCount = data.numberOfStudents || data.studentsData.length;
      const fontFamily = language === "fr" ? "helvetica" : "Amiri";

      doc.setFont(fontFamily, "normal");
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);

      let y = 12;
      if (language === "ar") {
        let rightY = y;
        doc.setFont("Amiri", "Bold");
        doc.text(labels.republic, rightMargin - doc.getTextWidth(labels.republic), rightY);
        rightY += 6;
        doc.setFont("Amiri", "normal");
        doc.text(labels.ministry, rightMargin - doc.getTextWidth(labels.ministry), rightY);
        rightY += 6;
        const delegationText = `${labels.delegation}${delegation ? ` بـ${delegation}` : ""}`;
        doc.text(delegationText, rightMargin - doc.getTextWidth(delegationText), rightY);
        rightY += 6;
        drawSchoolTypeHeader(doc, {
          level,
          schoolName,
          language,
          fontFamily: "Amiri",
          x: rightMargin,
          y: rightY,
          align: "right",
        });
        y = 12;
        if (this.pvGovLogoBase64) {
          doc.addImage(this.pvGovLogoBase64, "PNG", centerX - 5, y - 7, 10, 10);
        }
        y += 10;
        doc.setFont("Amiri", "Bold");
        doc.setFontSize(15);
        const sessionTitle = TRIMESTER_NAMES_AR[trimester] || labels.sessionTitle;
        doc.text(sessionTitle, centerX - doc.getTextWidth(sessionTitle) / 2, y);
        y += 8;
        doc.setFontSize(13);
        drawCenteredArabicLabelValue(doc, labels.class, className, centerX, y, {
          labelFont: "Amiri",
          fontSize: 13,
          fontStyle: "Bold",
        });
        doc.setFont("Amiri", "normal");
        doc.setFontSize(11);
        y += 7;
        const schoolYearText = `${labels.schoolYear} : ${schoolYear}`;
        doc.text(schoolYearText, leftMargin, y);
        doc.text(`${labels.studentsCount} : ${studentsCount}`, rightMargin - doc.getTextWidth(`${labels.studentsCount} : ${studentsCount}`), y);
      } else {
        let leftY = y;
        doc.setFont("helvetica", "Bold");
        doc.setFontSize(11);
        doc.text(labels.republic, leftMargin, leftY);
        leftY += 6;
        doc.setFont("helvetica", "normal");
        doc.text(labels.ministry, leftMargin, leftY);
        leftY += 6;
        drawSchoolTypeHeader(doc, {
          level,
          schoolName,
          language,
          fontFamily: "helvetica",
          x: leftMargin,
          y: leftY,
          align: "left",
        });
        leftY += 6;
        const delegationText = `${labels.delegation}${delegation ? ` ${delegation}` : ""}`;
        doc.setFont(getPdfFontForText(delegationText, "helvetica"), "normal");
        doc.text(delegationText, leftMargin, leftY);
        y = 12;
        if (this.pvGovLogoBase64) {
          doc.addImage(this.pvGovLogoBase64, "PNG", centerX - 5, y - 7, 10, 10);
        }
        y += 10;
        doc.setFont("helvetica", "Bold");
        doc.setFontSize(15);
        const sessionTitle = TRIMESTER_NAMES_FR[trimester] || labels.sessionTitle;
        doc.text(sessionTitle, centerX - doc.getTextWidth(sessionTitle) / 2, y);
        y += 8;
        doc.setFontSize(13);
        const classText = `${labels.class} : ${className}`;
        doc.text(classText, centerX - doc.getTextWidth(classText) / 2, y);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        y += 7;
        const schoolYearText = `${labels.schoolYear} : ${schoolYear}`;
        doc.text(`${labels.studentsCount} : ${studentsCount}`, leftMargin, y);
        doc.text(schoolYearText, rightMargin - doc.getTextWidth(schoolYearText), y);
      }

      const subjects = getSubjectRows(data.studentsData);
      const totalCoef = subjects.reduce((sum, subject) => sum + Number(subject.coef || 0), 0);
      const subjectNames = subjects.map((subject) =>
        language === "fr"
          ? subject.subject?.nameFr || subject.subject?.name || ""
          : subject.subject?.name || ""
      );
      const subjectCoefs = subjects.map((subject) => String(subject.coef ?? ""));

      const headRow = [
        labels.rank,
        labels.average,
        "",
        ...subjectNames.map(() => ""),
        labels.identifier,
        labels.name,
        labels.number,
      ];
      const totalColumnIndex = 2;
      const numberColumnIndex = headRow.length - 1;

      const body = sortStudentsByRank(data.studentsData).map((studentData, index) => {
        const subjectMarks = subjects.map((subjectColumn) => {
          const subjectRow = studentData.subjectMoyennes?.find(
            (item) => String(item.subject?._id) === String(subjectColumn.subject?._id)
          );
          return formatSubjectMark(subjectRow || {});
        });
        const student = studentData.student || {};
        const studentName =
          language === "fr"
            ? student.fullNameFr || student.fullName || ""
            : student.fullName || student.fullNameFr || "";
        return [
          studentData.rank == null ? "--" : String(studentData.rank),
          studentData.generalMoyenne == null
            ? "--.--"
            : formatMarkNumber(studentData.generalMoyenne),
          formatMarkNumber(calculateWeightedTotal(studentData.subjectMoyennes)),
          ...subjectMarks,
          student.identityNumber || "",
          studentName,
          String(index + 1),
        ];
      });

      const subjectStartIndex = 3;
      const subjectEndIndex = subjectStartIndex + subjects.length - 1;

      doc.autoTable({
        startY: y + 8,
        margin: { left: 6, right: 6 },
        head: [headRow],
        body,
        theme: "grid",
        styles: {
          font: fontFamily,
          fontSize: 7,
          halign: "center",
          valign: "middle",
          lineWidth: 0.2,
          lineColor: [0, 0, 0],
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: "Bold",
          lineColor: [0, 0, 0],
        },
        bodyStyles: {
          minCellHeight: 7,
          lineColor: [0, 0, 0],
        },
        didDrawCell: (cellData) => {
          if (cellData.section !== "head" || cellData.row.index !== 0) return;

          const isSubjectHeadColumn =
            cellData.column.index >= subjectStartIndex &&
            cellData.column.index <= subjectEndIndex;

          if (isSubjectHeadColumn) {
            const subjectIndex = cellData.column.index - subjectStartIndex;
            const label = subjectNames[subjectIndex];
            const coef = subjectCoefs[subjectIndex];
            if (!label) return;

            drawWrappedCellText(doc, label, cellData.cell, {
              defaultFontFamily: fontFamily,
              fontSize: 7,
              fontStyle: "Bold",
            });

            if (coef) {
              const x = cellData.cell.x + cellData.cell.width / 2;
              doc.setFont(fontFamily, "normal");
              doc.setFontSize(7);
              doc.text(coef, x, cellData.cell.y + cellData.cell.height - 2, {
                align: "center",
              });
            }
            return;
          }

          if (cellData.column.index === totalColumnIndex) {
            const x = cellData.cell.x + cellData.cell.width / 2;
            const bottomReserved = 5;
            const fontSize = 7;
            const lineHeight = getWrappedLineHeight(fontSize);
            const totalY =
              cellData.cell.y +
              Math.max(2, (cellData.cell.height - bottomReserved - lineHeight) / 2) +
              lineHeight * 0.75;
            doc.setFont(fontFamily, "Bold");
            doc.setFontSize(fontSize);
            doc.text(labels.total, x, totalY, { align: "center" });
            doc.setFont(fontFamily, "normal");
            doc.text(String(totalCoef), x, cellData.cell.y + cellData.cell.height - 2, {
              align: "center",
            });
          }
        },
        didParseCell: (cellData) => {
          const isSubjectHeadColumn =
            cellData.section === "head" &&
            cellData.column.index >= subjectStartIndex &&
            cellData.column.index <= subjectEndIndex;

          if (cellData.section === "head" && cellData.row.index === 0) {
            if (isSubjectHeadColumn) {
              const subjectIndex = cellData.column.index - subjectStartIndex;
              const label = subjectNames[subjectIndex];
              cellData.cell.text = [""];
              cellData.cell.styles.minCellHeight = estimateWrappedHeadHeight(
                doc,
                label,
                cellData.cell.width,
                fontFamily
              );
            } else if (cellData.column.index === totalColumnIndex) {
              cellData.cell.text = [""];
              cellData.cell.styles.minCellHeight = 24;
            }
          }

          if (
            cellData.section === "body" &&
            cellData.column.index === numberColumnIndex
          ) {
            cellData.cell.styles.fontStyle = "Bold";
          }

          const cellText = getCellText(cellData.cell);
          if (containsArabic(cellText)) {
            cellData.cell.styles.font = "Amiri";
          }
        },
      });

      const pages = doc.internal.getNumberOfPages();
      const pageHeight = doc.internal.pageSize.getHeight();
      for (let page = 1; page <= pages; page += 1) {
        doc.setPage(page);
        doc.setFontSize(8);
        const pageLabel = `${labels.pageWord} ${page} / ${pages}`;
        doc.setFont(getPdfFontForText(labels.pageWord, fontFamily), "normal");
        doc.text(pageLabel, pageWidth / 2, pageHeight - 5, {
          align: "center",
        });
      }

      return doc;
    },

    getPvLabels(language) {
      if (language === "fr") {
        return {
          republic: "République tunisienne",
          ministry: "Ministère de l'Éducation",
          delegation: "Délégation régionale de l'éducation",
          studentsCount: "Nombre d'élèves",
          schoolYear: "Année scolaire",
          class: "Classe",
          sessionTitle: "Procès-verbal",
          number: "N°",
          name: "Nom et prénom",
          identifier: "Identifiant",
          total: "Total",
          average: "Moyenne",
          rank: "Rang",
          pageWord: "Page",
        };
      }

      return {
        republic: "الجمهورية التونسية",
        ministry: "وزارة التربية",
        delegation: "المندوبية الجهوية للتربية",
        studentsCount: "عدد التلاميذ",
        schoolYear: "السنة الدراسية",
        class: "القسم",
        sessionTitle: "محضر جلسة",
        number: "الرقم",
        name: "الإسم و اللقب",
        identifier: "المعرف",
        total: "الجملة",
        average: "المعدل",
        rank: "الرتبة",
        pageWord: "صفحة",
      };
    },
  },
};
